import React, { type ForwardedRef, forwardRef } from 'react';
import {
	TypeSelect,
	TypeSelectWithAddIssueTypeModal,
	useManageIssueTypeOption,
	type TypeSelectProps,
	type TypeSelectRef,
} from '@atlassian/jira-config-in-context-issue-types';
import { UNSAFE_noExposureExp } from '@atlassian/jira-feature-experiments';
import type { InlineCardCreateProps } from '@atlassian/jira-platform-board-kit';
import { AsyncInlineCardCreate } from '@atlassian/jira-platform-board-kit/async';
import { useAddIssueTypeContext } from '@atlassian/jira-project-settings-apps-add-issue-type';
import { REFRESH_SOURCE_ADD_ISSUE_TYPE_MODAL } from '../../../../../../model/constants';
import { CAN_EDIT_BOARD } from '../../../../../../model/permission/permission-types';
import { useBoardSelector, useBoardDispatch } from '../../../../../../state';
import { workRefreshData } from '../../../../../../state/actions/work';
import { getPermissionsSelector } from '../../../../../../state/selectors/board/board-permissions-selectors';
import { manageIssueTypesLinkSelector } from '../../../../../../state/selectors/card-create/card-create-selectors';

const IssueTypeCreatableTypeSelect = forwardRef(
	(props: TypeSelectProps, ref: ForwardedRef<TypeSelectRef>) => {
		const dispatch = useBoardDispatch();

		const manageIssueTypesLink = useBoardSelector((state) =>
			getPermissionsSelector(state)[CAN_EDIT_BOARD] ? manageIssueTypesLinkSelector(state) : null,
		);
		const manageIssueTypeOption = useManageIssueTypeOption(manageIssueTypesLink);

		const handleNewIssueTypeCreate = () => {
			// handle refresh/dispatch
			dispatch(workRefreshData(REFRESH_SOURCE_ADD_ISSUE_TYPE_MODAL));
		};

		return (
			<TypeSelectWithAddIssueTypeModal
				{...props}
				ref={ref}
				onNewIssueTypeCreated={handleNewIssueTypeCreate}
				extraOptions={manageIssueTypeOption ?? undefined}
			/>
		);
	},
);

const BasicTypeSelect = forwardRef((props: TypeSelectProps, ref: ForwardedRef<TypeSelectRef>) => {
	const manageIssueTypesLink = useBoardSelector((state) =>
		getPermissionsSelector(state)[CAN_EDIT_BOARD] ? manageIssueTypesLinkSelector(state) : null,
	);
	const manageIssueTypeOption = useManageIssueTypeOption(manageIssueTypesLink);

	return <TypeSelect {...props} ref={ref} extraOptions={manageIssueTypeOption ?? undefined} />;
});

const AsyncInlineCardCreateWithIssueTypesInContextVariant = ({
	hasAddIssueTypeOption,
	isEligibleInContextIssueTypesExperiment,
	...inlineCardCreateProps
}: InlineCardCreateProps & {
	hasAddIssueTypeOption: boolean;
	isEligibleInContextIssueTypesExperiment: boolean;
}) => {
	const [{ isActive: isModalOpen }] = useAddIssueTypeContext();

	return (
		<AsyncInlineCardCreate
			{...inlineCardCreateProps}
			TypeSelect={hasAddIssueTypeOption ? IssueTypeCreatableTypeSelect : BasicTypeSelect}
			shouldKeepFormOpenOnClickOutside={isModalOpen}
			isEligibleInContextIssueTypesExperiment={isEligibleInContextIssueTypesExperiment}
		/>
	);
};

export const AsyncInlineCardCreateWithIssueTypesInContext = ({
	hasAddIssueTypeOption,
	...inlineCardCreateProps
}: InlineCardCreateProps & { hasAddIssueTypeOption: boolean }) => {
	// Variant cohort
	const [expConfig] = UNSAFE_noExposureExp('jsw_config_in_context_issue_types_m2');
	if (expConfig.get('inlineIssueTypeConfigEnabled', false)) {
		return (
			<AsyncInlineCardCreateWithIssueTypesInContextVariant
				{...inlineCardCreateProps}
				isEligibleInContextIssueTypesExperiment
				hasAddIssueTypeOption={hasAddIssueTypeOption}
			/>
		);
	}

	// Control cohort
	return (
		<AsyncInlineCardCreate {...inlineCardCreateProps} isEligibleInContextIssueTypesExperiment />
	);
};
